/**
 * Login happens in the house, and the house sends you back to your product.
 *
 * WHY ONE LOGIN PAGE. Each product shell has its own hostname, and the session
 * is a `.lumnian.com` cookie, so signing in on any host signs you in on all of
 * them — but a login FORM on four hosts is four places for the same screen to
 * drift, and a bookmarked `beats.lumnian.com/login` reads as though Beats had
 * its own account. So an unauthenticated visit to a product host bounces to
 * `app.<domain>/login?next=<where you were>`, and after signing in the house
 * hands you back — or, with nowhere to hand you back to, to the product you
 * used last time.
 *
 * "LAST APP" IS A COOKIE, NOT localStorage. `localStorage` is origin-scoped:
 * something written at `beats.lumnian.com` is invisible at `app.lumnian.com`,
 * which is exactly where the login page has to read it. `crossShellPrefs`
 * exists for this — the theme had the same problem — so the last shell is
 * stored the way the theme is.
 *
 * NOT HELD MEANS THE HOUSE. A `next` pointing at a product you have not bought,
 * or a remembered shell whose entitlement has lapsed, lands you in the house,
 * where the launcher shows that product locked. Sending someone into a shell
 * that will 402 every request is worse than the launcher.
 *
 * `next` IS VALIDATED, NOT TRUSTED. It is a URL in the query string, which
 * means anyone can put one there. Only a sibling host on our own domain with
 * our scheme and port is followed; anything else is ignored, so this cannot be
 * used as an open redirect.
 *
 * Pure and dependency-free so CRA's jest can test it.
 */
import type { Product } from '../types';
import { productOfShell, Shell, SHELLS, shellFromHost } from './shell';
import { shellUrl } from './products';
import { withResume } from './lastSpot';

/** The `crossShellPrefs` key holding the last product shell someone worked in. */
export const LAST_SHELL_KEY = 'jcseo_last_shell';

/**
 * `beats.lumnian.com` -> `lumnian.com`; null for a single-label host or an IP.
 *
 * Only a SHELL label comes off. Dropping whatever label came first turned the
 * apex `lumnian.com` into the "domain" `com`, and then `https://app.com/`
 * passed as a sibling: an open redirect. A host that is not a shell host is
 * its own domain.
 */
function domainOf(hostname: string): string | null {
  const labels = (hostname || '').toLowerCase().split('.');
  if (labels.length < 2) return null;
  if (/^\d+$/.test(labels[labels.length - 1])) return null;
  return labels[0] in SHELLS ? labels.slice(1).join('.') : labels.join('.');
}

/**
 * The house login URL to send an unauthenticated visitor to, carrying where
 * they were so they can be sent back — or null when they should simply see
 * this host's own `/login`.
 *
 * Null covers the house itself (a router redirect is right there) and a host
 * with no siblings (`localhost` serves every shell from one origin, so there
 * is no house to go to).
 */
export function houseLoginUrl(
  hostname: string,
  origin: string,
  returnTo: string,
): string | null {
  if (shellFromHost(hostname) === 'app') return null;
  if (!domainOf(hostname)) return null;
  try {
    const url = new URL(shellUrl('app', hostname, origin));
    url.pathname = '/login';
    url.search = returnTo ? `?next=${encodeURIComponent(returnTo)}` : '';
    return url.toString();
  } catch {
    return null;
  }
}

/**
 * Where a `next` parameter points, if it points somewhere we are willing to go.
 *
 * Accepts only `<shell>.<our domain>` on our scheme and port. A path-only
 * `next` counts as the house itself, since that is where the login page is.
 */
export function siblingTarget(
  next: string | null | undefined,
  hostname: string,
  origin: string,
): { shell: Shell; href: string } | null {
  if (!next) return null;
  try {
    const here = new URL(origin);
    const target = new URL(next, origin);
    if (target.protocol !== here.protocol || target.port !== here.port) return null;
    if (target.hostname === here.hostname) {
      return { shell: shellFromHost(here.hostname), href: target.toString() };
    }
    const domain = domainOf(hostname);
    if (!domain) return null;
    const [label, ...rest] = target.hostname.split('.');
    if (rest.join('.') !== domain) return null;
    if (!(label in SHELLS)) return null;
    return { shell: label as Shell, href: target.toString() };
  } catch {
    return null;
  }
}

export type Destination =
  /** Leave the SPA: another host, a full page load. */
  | { kind: 'href'; href: string }
  /** Stay in the router. */
  | { kind: 'path'; to: string };

/**
 * Where to go once signed in at the house.
 *
 *   1. `next`, when it is a sibling shell whose product is held (or the house).
 *      A `next` we will not follow ends in the house — deliberately not falling
 *      through to the remembered shell, because the person asked for somewhere
 *      specific and the honest answer is the launcher with that product locked.
 *   2. Otherwise the remembered shell, when its product is still held.
 *   3. Otherwise `fallback` — the router path the house was protecting.
 */
export function afterLoginDestination(args: {
  next: string | null | undefined;
  lastShell: string | null | undefined;
  held: Product[];
  hostname: string;
  origin: string;
  fallback: string;
}): Destination {
  const { next, lastShell, held, hostname, origin, fallback } = args;
  const allowed = (shell: Shell) => {
    const product = productOfShell(shell);
    return product === null || held.includes(product);
  };

  const target = siblingTarget(next, hostname, origin);
  if (target) {
    if (!allowed(target.shell)) return { kind: 'path', to: '/' };
    try {
      const url = new URL(target.href);
      if (url.origin === new URL(origin).origin) {
        return { kind: 'path', to: `${url.pathname}${url.search}${url.hash}` };
      }
    } catch {
      // fall through to a full load
    }
    return { kind: 'href', href: target.href };
  }
  if (next) return { kind: 'path', to: '/' }; // present but not ours: the house

  if (lastShell && lastShell in SHELLS && lastShell !== 'app') {
    const shell = lastShell as Shell;
    if (allowed(shell)) {
      // MARKED TO RESUME: the product goes on to the page it remembers this
      // person was on (`lastSpot`), which only it can read.
      const home = withResume(SHELLS[shell].home);
      const base = shellUrl(shell, hostname, origin);
      // On a single-label host the shell URL is this origin, so a full load
      // would only reload the app to land on a path the router can reach.
      if (base === origin) return { kind: 'path', to: home };
      return { kind: 'href', href: `${base}${home}` };
    }
  }

  return { kind: 'path', to: fallback || '/' };
}
